import { observer } from "mobx-react";
import { CompanyStatusLabel } from "../../../OrganizationalStructure/components/company-status-label";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../modals/styles/EmissionActivityModal.module.css";
import Select from "react-select";
import commonStore from "../../../Store/marco/commonStore";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
import { getSelectComOptionByCompanyList } from "../../utils/getSelectComOption";
import { getSelectComOptionByOrgList } from "../../utils/getSelectComOption";

const selectComStyle = {
  control: (base, { isFocused }) => ({
    ...base,
    cursor: "pointer",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    width: "732px",
    minHeight: "43px", // minHeight 取代固定 height，讓 hint 不被截斷
    height: "43px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "none",
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#9ca3af",
      boxShadow: "none",
    },
    ...(isFocused
      ? {
          borderColor: "#6b7280",
          boxShadow: "0 0 0 2px rgba(107, 114, 128, 0.15)",
        }
      : {}),
  }),
  input: (base) => ({
    ...base,
    padding: "0",
    margin: "0",
    fontSize: "14px",
    color: "#374151",
  }),
  menu: (base) => ({
    ...base,
    padding: "4px 0",
    width: "732px",
    borderRadius: "6px",
    marginTop: "4px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0",
    paddingLeft: "12px",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "240px",
    overflowY: "auto",
    padding: "0",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "14px",
    color: "#374151",
    fontWeight: "450",
    maxWidth: "calc(100% - 24px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#eff6ff"
      : state.isFocused
        ? "#f3f4f6"
        : "#fff",
    color: state.isSelected ? "#1d4ed8" : "#374151",
    padding: "10px 12px", // 加大 padding，配合雙行 hierarchy hint 顯示
    cursor: "pointer",
    fontSize: "14px",
    lineHeight: "1.4",
    transition: "background-color 0.12s ease",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "14px", // 統一為 14px
    fontStyle: "normal",
    fontWeight: "normal",
  }),
};

export const SelectCom = observer(({ schema, isEdit, value, target = "EFFormData" }) => {
  const { currentCId, _handleSetEAFormData, _handleSetEFFormData } =
    emissionViewStore;
  const cmStore = commonStore;
  const inventoryCompanyList = emissionViewStore.inventoryCompanyList;

  const { t } = useTranslation("ActivityMain");

  //////// 一次性從 localStorage 解析 companyList，建成 Map<name, {path, type}>
  const hierarchyMap = useMemo(() => {
    try {
      const list = cmStore.companyList;
      if (!list || list.length === 0) return new Map();
      return new Map(
        list.map((item) => [
          item.id,
          {
            path: item.hierarchy_name_path ?? "",
          },
        ]),
      );
    } catch {
      return new Map();
    }
  }, [cmStore.companyList]);

  //////// 自訂選項渲染：O(1) Map 查詢，不再每次 hover 重新 parse
  const formatOptionLabel = useCallback(
    (option) => {
      const data = hierarchyMap.get(option.value) || { path: "" };
      const { path } = data;
      return (
        <div style={{ lineHeight: "1.4" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              width: "100%",
            }}
          >
            <span style={{ fontSize: "14px", color: "inherit" }}>
              <CompanyStatusLabel id={option.value} name={option.label} />
            </span>
          </div>
          {path && (
            <div
              style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}
            >
              {path}
            </div>
          )}
        </div>
      );
    },
    [hierarchyMap, t],
  );

  const selectComOptions = useMemo(() => {
    if (cmStore.scopeType === "COMPANY") {
      return getSelectComOptionByCompanyList(
        inventoryCompanyList,
        currentCId,
        cmStore.role,
      );
    } else {
      return getSelectComOptionByOrgList(
        cmStore.orgList,
        currentCId,
        cmStore.rootId,
      );
    }
  }, [inventoryCompanyList, cmStore.orgList, currentCId, cmStore.rootId, cmStore.role, cmStore.scopeType]);

  ////////選中的option
  const selectedOption = useMemo(
    () =>
      selectComOptions.find((opt) => {
        const ov = opt.value;
        const vv = value;
        // 1. 如果都是数组，就用 toString() 或者逐项比较
        if (Array.isArray(ov) && Array.isArray(vv)) {
          return ov.toString() === vv.toString();
        }
        // 2. 否则直接做严格比较（支持 string、number、boolean）
        return ov === vv;
      }) || null,
    [selectComOptions, value],
  );

  ////////如果下拉式選單中只有一間公司 自動填入
  useEffect(() => {
    if (isEdit) return;
    if (selectComOptions.length !== 1) return;

    const only = selectComOptions[0];
    _handleSetEAFormData(schema, "selectedCom", "companyId", only.value, null);
    _handleSetEFFormData(
      schema,
      "selectedCom",
      "companyId",
      only.value,
      null,
      target,
    );
  }, [isEdit, selectComOptions]);

  return (
    <div className={styles.selectComWrap}>
      <label>
        <span style={{ color: "red" }}>* </span>
        {t("Select Company")}
      </label>
      <Select
        options={selectComOptions}
        isDisabled={isEdit}
        value={selectedOption}
        placeholder={null}
        formatOptionLabel={formatOptionLabel}
        onChange={(e) => {
          _handleSetEAFormData(schema, "selectedCom", "companyId", e.value);
          _handleSetEFFormData(
            schema,
            "selectedCom",
            "companyId",
            e.value,
            null,
            target,
          );
        }}
        styles={selectComStyle}
        components={{
          IndicatorSeparator: () => null, // 移除分隔线
        }}
      />
    </div>
  );
});
